/**
 * RemediationStepEditor.jsx
 *
 * Editable list of remediation wizard steps. For each step it renders:
 *   - name / description / action-type picker
 *   - dynamic config fields driven by remediationActionMeta (so the exact payload that
 *     will run is visible and editable — including inline script bodies)
 *   - advanced execution controls (retry count/delay, timeout, continue-on-failure)
 *   - an execution-condition gate (run only when previous step Succeeded/Failed)
 *   - a Script Library picker for RunScript steps (load a stored script body inline)
 *   - add / remove / reorder controls
 *
 * Fully controlled: the parent owns the `steps` array and receives changes via onChange.
 *
 * @module SettingsPages/components/remediation/RemediationStepEditor
 */

import { useState } from 'react';
import * as FaIcons from 'react-icons/fa';
import {
    ACTION_META, ACTION_META_MAP, parseConfig, parseCondition,
    serializeCondition, emptyStep
} from './remediationActionMeta';

const {
    FaPlus, FaTrashAlt, FaChevronDown, FaChevronUp, FaArrowUp, FaArrowDown,
    FaListOl, FaBolt, FaShieldAlt
} = FaIcons;

/** Resolve a fa icon name string to its component, falling back to a bolt. */
const iconFor = (name) => FaIcons[name] || FaBolt;

const CONDITION_OPERATORS = [
    { value: 'Always', label: 'Always run' },
    { value: 'Equals', label: 'Run when previous step is…' },
    { value: 'NotEquals', label: 'Run when previous step is NOT…' },
    { value: 'Never', label: 'Never (disabled)' }
];

const RemediationStepEditor = ({ steps, onChange, scripts = [] }) => {
    const [expandedIndex, setExpandedIndex] = useState(0);
    // Tracks which allow-custom select fields the user has switched into free-text
    // ("Other…") mode, keyed by the field's DOM id (e.g. "step-0-channel").
    const [customFields, setCustomFields] = useState({});

    const updateStep = (index, patch) => {
        const next = steps.map((s, i) => (i === index ? { ...s, ...patch } : s));
        onChange(next);
    };

    /** Merge a single config field into the step's actionConfigJson. */
    const updateConfigField = (index, key, value) => {
        const step = steps[index];
        const cfg = parseConfig(step.actionConfigJson);
        if (value === '' || value === null || value === undefined) {
            delete cfg[key];
        } else {
            cfg[key] = value;
        }
        const json = Object.keys(cfg).length > 0 ? JSON.stringify(cfg) : null;
        updateStep(index, { actionConfigJson: json });
    };

    const updateCondition = (index, patch) => {
        const step = steps[index];
        const current = parseCondition(step.executionConditionJson);
        const merged = { ...current, ...patch };
        updateStep(index, { executionConditionJson: serializeCondition(merged) });
    };

    const addStep = () => {
        const next = [...steps, emptyStep(steps.length)];
        onChange(next);
        setExpandedIndex(next.length - 1);
    };

    const removeStep = (index) => {
        const next = steps.filter((_, i) => i !== index).map((s, i) => ({ ...s, sortOrder: i }));
        onChange(next);
        setExpandedIndex(-1);
    };

    const moveStep = (index, dir) => {
        const target = index + dir;
        if (target < 0 || target >= steps.length) return;
        const next = [...steps];
        [next[index], next[target]] = [next[target], next[index]];
        const reindexed = next.map((s, i) => ({ ...s, sortOrder: i }));
        onChange(reindexed);
        setExpandedIndex(target);
    };

    const loadScriptIntoStep = (index, scriptId) => {
        const script = scripts.find(s => String(s.remediationScriptId) === String(scriptId));
        if (!script) return;
        const step = steps[index];
        const cfg = parseConfig(step.actionConfigJson);
        cfg.script = script.scriptBody;
        cfg.platform = script.platform || cfg.platform || 'PowerShell';
        updateStep(index, {
            actionConfigJson: JSON.stringify(cfg),
            // Default the step name to the script's name when not yet set.
            name: step.name?.trim() ? step.name : script.name
        });
    };

    const renderField = (index, step, field) => {
        const cfg = parseConfig(step.actionConfigJson);
        const value = cfg[field.key] ?? '';
        const id = `step-${index}-${field.key}`;

        if (field.type === 'select') {
            // For allow-custom fields, any stored value that isn't one of the known
            // options is treated as a custom entry, so existing wizards that persisted
            // a bespoke value still round-trip correctly.
            const isCustomValue = !!field.allowCustom && value !== '' && !field.options.includes(value);
            const inCustomMode = field.allowCustom && (customFields[id] || isCustomValue);
            const selectValue = inCustomMode ? '__custom__' : value;

            const onSelectChange = (e) => {
                const next = e.target.value;
                if (next === '__custom__') {
                    // Switch this field into free-text mode without clobbering any
                    // value already typed; the revealed input drives the actual config.
                    setCustomFields(prev => ({ ...prev, [id]: true }));
                    return;
                }
                setCustomFields(prev => ({ ...prev, [id]: false }));
                updateConfigField(index, field.key, next);
            };

            return (
                <div className="form-group" key={field.key}>
                    <label htmlFor={id}>{field.label}</label>
                    <select id={id} className="form-control" value={selectValue}
                        onChange={onSelectChange}>
                        <option value="">— Select —</option>
                        {field.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        {field.allowCustom && <option value="__custom__">Other…</option>}
                    </select>
                    {inCustomMode && (
                        <input
                            id={`${id}-custom`}
                            type="text"
                            className="form-control"
                            style={{ marginTop: 6 }}
                            value={value}
                            placeholder={field.placeholder || 'Enter a custom value'}
                            aria-label={`${field.label} (custom value)`}
                            onChange={(e) => updateConfigField(index, field.key, e.target.value)}
                        />
                    )}
                    {field.help && <small className="field-help">{field.help}</small>}
                </div>
            );
        }

        if (field.type === 'textarea' || field.type === 'code') {
            const isScript = field.type === 'code' && field.key === 'script';
            return (
                <div className="form-group" key={field.key} style={{ gridColumn: '1 / -1' }}>
                    {isScript ? (
                        <div className="script-field-toolbar">
                            <label htmlFor={id}>{field.label}</label>
                            {scripts.length > 0 && (
                                <span className="script-picker">
                                    <FaListOl size={11} />
                                    <select aria-label="Load from script library" defaultValue=""
                                        onChange={(e) => { loadScriptIntoStep(index, e.target.value); e.target.value = ''; }}>
                                        <option value="">Load from library…</option>
                                        {scripts.map(s => (
                                            <option key={s.remediationScriptId} value={s.remediationScriptId}>
                                                {s.name} ({s.platform})
                                            </option>
                                        ))}
                                    </select>
                                </span>
                            )}
                        </div>
                    ) : (
                        <label htmlFor={id}>{field.label}</label>
                    )}
                    <textarea
                        id={id}
                        className={`form-control ${field.type === 'code' ? 'code-textarea' : ''}`}
                        rows={field.rows || 3}
                        value={value}
                        placeholder={field.placeholder}
                        onChange={(e) => updateConfigField(index, field.key, e.target.value)}
                    />
                    {field.help && <small className="field-help">{field.help}</small>}
                </div>
            );
        }

        // text / number
        return (
            <div className="form-group" key={field.key}>
                <label htmlFor={id}>{field.label}</label>
                <input
                    id={id}
                    type={field.type === 'number' ? 'number' : 'text'}
                    className="form-control"
                    value={value}
                    placeholder={field.placeholder}
                    onChange={(e) => updateConfigField(index, field.key, e.target.value)}
                />
                {field.help && <small className="field-help">{field.help}</small>}
            </div>
        );
    };

    return (
        <div className="remediation-step-editor">
            <div className="step-editor-header">
                <h4><FaListOl /> Wizard Steps ({steps.length})</h4>
                <div className="step-editor-actions">
                    <button type="button" className="btn-secondary" onClick={addStep}>
                        <FaPlus /> Add Step
                    </button>
                </div>
            </div>

            {steps.length === 0 && (
                <div className="steps-empty">
                    No steps yet. Add the actions this wizard should run when triggered — scripts,
                    device actions, notifications, approvals and more.
                </div>
            )}

            {steps.map((step, index) => {
                const meta = ACTION_META_MAP[step.actionType] || ACTION_META[0];
                const StepIcon = iconFor(meta.icon);
                const expanded = expandedIndex === index;
                const condition = parseCondition(step.executionConditionJson);
                const hasSideEffect = !!meta.capability;

                return (
                    <div className="remediation-step-card" key={step.endpointRemediationStepId || `new-${index}`}>
                        <div
                            className="step-card-head"
                            role="button"
                            tabIndex={0}
                            aria-expanded={expanded}
                            onClick={() => setExpandedIndex(expanded ? -1 : index)}
                            onKeyDown={(e) => {
                                // Toggle expand/collapse on Enter or Space so the header is
                                // operable for keyboard-only and screen-reader users.
                                if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
                                    e.preventDefault();
                                    setExpandedIndex(expanded ? -1 : index);
                                }
                            }}
                        >
                                <span className="step-index">{index + 1}</span>
                                <div className="step-title">
                                    <div className="step-name" title={step.name?.trim() || 'Untitled step'}>
                                        {step.name?.trim() || 'Untitled step'}
                                    </div>
                                    <div className="step-action-type">
                                        <StepIcon size={11} /> {meta.label}
                                        <span
                                            className={`step-type-chip${hasSideEffect ? '' : ' step-type-chip--safe'}`}
                                            title={hasSideEffect ? `Requires ${meta.capability} capability` : 'No external side effect'}
                                        >
                                            {hasSideEffect ? meta.capability : 'No side effect'}
                                        </span>
                                    </div>
                                </div>
                                <div className="step-head-controls">
                                <button type="button" className="btn-icon btn-reorder" title="Move up"
                                    disabled={index === 0}
                                    onClick={(e) => { e.stopPropagation(); moveStep(index, -1); }}>
                                    <FaArrowUp size={12} />
                                </button>
                                <button type="button" className="btn-icon btn-reorder" title="Move down"
                                    disabled={index === steps.length - 1}
                                    onClick={(e) => { e.stopPropagation(); moveStep(index, 1); }}>
                                    <FaArrowDown size={12} />
                                </button>
                                <button type="button" className="btn-icon btn-danger" title="Remove step"
                                    onClick={(e) => { e.stopPropagation(); removeStep(index); }}>
                                    <FaTrashAlt size={12} />
                                </button>
                                <button type="button" className="btn-icon" title={expanded ? 'Collapse' : 'Expand'}
                                    onClick={(e) => { e.stopPropagation(); setExpandedIndex(expanded ? -1 : index); }}>
                                    {expanded ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                                </button>
                            </div>
                        </div>

                        {expanded && (
                            <div className="step-card-body">
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor={`step-${index}-name`}>Step Name</label>
                                        <input id={`step-${index}-name`} type="text" className="form-control"
                                            value={step.name} placeholder="e.g. Clear temp files"
                                            onChange={(e) => updateStep(index, { name: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor={`step-${index}-action`}>Action Type</label>
                                        <select id={`step-${index}-action`} className="form-control"
                                            value={step.actionType}
                                            onChange={(e) => updateStep(index, { actionType: e.target.value })}>
                                            {ACTION_META.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                                        </select>
                                        <small className="field-help">{meta.description}</small>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label htmlFor={`step-${index}-desc`}>Description</label>
                                    <input id={`step-${index}-desc`} type="text" className="form-control"
                                        value={step.description || ''} placeholder="Optional — what this step does"
                                        onChange={(e) => updateStep(index, { description: e.target.value })} />
                                </div>

                                {/* Dynamic config fields for the selected action type */}
                                {meta.fields.length > 0 && (
                                    <div className="step-config-fields">
                                        <div className="form-row" style={{ flexWrap: 'wrap' }}>
                                            {meta.fields.map(field => renderField(index, step, field))}
                                        </div>
                                    </div>
                                )}

                                {/* Execution condition gate */}
                                <fieldset style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 12px', margin: '10px 0' }}>
                                    <legend style={{ fontWeight: 600, fontSize: '0.78rem', padding: '0 6px', color: '#374151' }}>
                                        <FaShieldAlt size={11} /> Run Condition
                                    </legend>
                                    <div className="step-condition-row">
                                        <div className="form-group">
                                            <label htmlFor={`step-${index}-cond-op`}>When</label>
                                            <select id={`step-${index}-cond-op`} className="form-control"
                                                value={condition.operator}
                                                onChange={(e) => updateCondition(index, { operator: e.target.value })}>
                                                {CONDITION_OPERATORS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                            </select>
                                        </div>
                                        {(condition.operator === 'Equals' || condition.operator === 'NotEquals') && (
                                            <div className="form-group">
                                                <label htmlFor={`step-${index}-cond-val`}>Previous step outcome</label>
                                                <select id={`step-${index}-cond-val`} className="form-control"
                                                    value={condition.value}
                                                    onChange={(e) => updateCondition(index, { value: e.target.value })}>
                                                    <option value="Success">Success</option>
                                                    <option value="Failed">Failed</option>
                                                </select>
                                            </div>
                                        )}
                                    </div>
                                </fieldset>

                                {/* Advanced execution controls */}
                                <fieldset style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 12px', marginBottom: 10 }}>
                                    <legend style={{ fontWeight: 600, fontSize: '0.78rem', padding: '0 6px', color: '#374151' }}>
                                        <FaBolt size={11} /> Execution Options
                                    </legend>
                                    <div className="step-advanced-grid">
                                        <div className="form-group">
                                            <label htmlFor={`step-${index}-retry`}>Retry Count</label>
                                            <input id={`step-${index}-retry`} type="number" min={0} max={10} className="form-control"
                                                value={step.retryCount}
                                                onChange={(e) => updateStep(index, { retryCount: parseInt(e.target.value, 10) || 0 })} />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor={`step-${index}-retrydelay`}>Retry Delay (s)</label>
                                            <input id={`step-${index}-retrydelay`} type="number" min={0} className="form-control"
                                                value={step.retryDelaySeconds}
                                                onChange={(e) => updateStep(index, { retryDelaySeconds: parseInt(e.target.value, 10) || 0 })} />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor={`step-${index}-timeout`}>Timeout (s)</label>
                                            <input id={`step-${index}-timeout`} type="number" min={0} className="form-control"
                                                value={step.timeoutSeconds}
                                                onChange={(e) => updateStep(index, { timeoutSeconds: parseInt(e.target.value, 10) || 0 })} />
                                        </div>
                                        <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                                                <input type="checkbox" checked={step.continueOnFailure}
                                                    onChange={(e) => updateStep(index, { continueOnFailure: e.target.checked })} />
                                                Continue on failure
                                            </label>
                                        </div>
                                    </div>
                                </fieldset>

                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                    <input type="checkbox" checked={step.isActive}
                                        onChange={(e) => updateStep(index, { isActive: e.target.checked })} />
                                    Step is active
                                </label>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default RemediationStepEditor;
